/**
 * Brewing stand block entity (vanilla BrewingStandBlockEntity.serverTick rules).
 * DOM-free and serializable; keys are "x,y,z" (the world adds a dimension prefix when saving).
 *
 *   state.tick()      one 20 Hz tick; true when the number of bottles brewing flipped
 *   world.get(key)    state for a stand (created on first use)
 *   serialize()/load  flat arrays: [bottle0(3) bottle1(3) bottle2(3) ingredient(3) fuel(3) brewTime fuelLeft]
 *
 * Slots: 0..2 bottles, 3 ingredient, 4 blaze powder fuel. A brew takes 400 ticks (20 s) and
 * costs one fuel charge; one blaze powder is 20 charges.
 */

import { clearStack, emptyStack, isEmpty, type Stack } from '../types';
import { I } from '../itemIds';
import { brewResult, isBrewIngredient, isPotionItem } from './potions';

export const BREW_TICKS = 400;
export const FUEL_CHARGES = 20;

export class BrewingStandState {
  /** Three bottle slots. */
  readonly bottles: Stack[] = [emptyStack(), emptyStack(), emptyStack()];
  ingredient: Stack = emptyStack();
  fuel: Stack = emptyStack();
  /** Ticks of brewing left (0 = idle). */
  brewTime = 0;
  /** Remaining brews on the current blaze powder (0..20). */
  fuelLeft = 0;
  /** Ingredient id the running brew started with (an item swap restarts it). */
  private brewing = 0;

  /** Can the ingredient change at least one bottle right now? */
  canBrew(): boolean {
    if (isEmpty(this.ingredient) || !isBrewIngredient(this.ingredient.id)) return false;
    return this.bottles.some((b) => !isEmpty(b) && brewResult(this.ingredient.id, b) !== null);
  }

  /** Bottles occupying slots (the three bubbles / bottle sprites on the block). */
  get bottleMask(): number {
    return (isEmpty(this.bottles[0]) ? 0 : 1) | (isEmpty(this.bottles[1]) ? 0 : 2) | (isEmpty(this.bottles[2]) ? 0 : 4);
  }

  tick(): boolean {
    const before = this.bottleMask;
    // refuel from the blaze powder slot
    if (this.fuelLeft <= 0 && this.fuel.id === I.BLAZE_POWDER && !isEmpty(this.fuel)) {
      this.fuelLeft = FUEL_CHARGES;
      this.fuel.count--;
      if (this.fuel.count <= 0) clearStack(this.fuel);
    }
    const can = this.canBrew();
    if (this.brewTime > 0) {
      this.brewTime--;
      if (this.brewTime === 0 && can) this.finish();
      else if (!can || this.brewing !== this.ingredient.id) this.brewTime = 0;
    } else if (can && this.fuelLeft > 0) {
      this.fuelLeft--;
      this.brewTime = BREW_TICKS;
      this.brewing = this.ingredient.id;
    }
    return before !== this.bottleMask;
  }

  /** Brew every convertible bottle and use up one ingredient. */
  private finish(): void {
    const ing = this.ingredient.id;
    for (const b of this.bottles) {
      if (isEmpty(b)) continue;
      const r = brewResult(ing, b);
      if (r) { b.id = r.id; b.dmg = r.dmg; }
    }
    this.ingredient.count--;
    if (this.ingredient.count <= 0) clearStack(this.ingredient);
  }

  /** Progress 0..1 of the running brew (for the arrow / bubbles). */
  get progress(): number { return this.brewTime > 0 ? 1 - this.brewTime / BREW_TICKS : 0; }

  get isIdleEmpty(): boolean {
    return this.bottles.every(isEmpty) && isEmpty(this.ingredient) && isEmpty(this.fuel) && this.brewTime === 0 && this.fuelLeft === 0;
  }

  serialize(): number[] {
    const s = (x: Stack): number[] => (isEmpty(x) ? [0, 0, 0] : [x.id, x.count, x.dmg]);
    return [...this.bottles.flatMap(s), ...s(this.ingredient), ...s(this.fuel), this.brewTime, this.fuelLeft, this.brewing];
  }

  static from(flat?: number[]): BrewingStandState {
    const f = new BrewingStandState();
    if (!flat || flat.length < 17) return f;
    const n = (i: number): number => (Number.isFinite(flat[i]) ? flat[i] : 0);
    const put = (dst: Stack, o: number): void => {
      dst.id = n(o); dst.count = n(o + 1); dst.dmg = n(o + 2);
      if (isEmpty(dst)) clearStack(dst);
    };
    f.bottles.forEach((b, i) => put(b, i * 3));
    put(f.ingredient, 9); put(f.fuel, 12);
    f.brewTime = Math.max(0, n(15));
    f.fuelLeft = Math.max(0, Math.min(FUEL_CHARGES, n(16)));
    f.brewing = n(17);
    return f;
  }
}

export const mayPlaceBottle = (s: Stack): boolean => isPotionItem(s.id) || s.id === I.GLASS_BOTTLE;

export class BrewingWorld {
  private readonly map = new Map<string, BrewingStandState>();

  get(key: string): BrewingStandState {
    let s = this.map.get(key);
    if (!s) { s = new BrewingStandState(); this.map.set(key, s); }
    return s;
  }
  peek(key: string): BrewingStandState | undefined { return this.map.get(key); }
  remove(key: string): BrewingStandState | undefined {
    const s = this.map.get(key);
    this.map.delete(key);
    return s;
  }

  tick(): void { for (const s of this.map.values()) s.tick(); }

  /** Non-empty stands, keys prefixed with `dim:` so every dimension shares one blob. */
  serialize(dim: string): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    for (const [k, s] of this.map) if (!s.isIdleEmpty) out[`${dim}:${k}`] = s.serialize();
    return out;
  }

  /** Replace the contents with the stands of `dim` found in `all` (other dimensions' keys are ignored). */
  load(all: Record<string, number[]> | undefined | null, dim: string): void {
    this.map.clear();
    const p = dim + ':';
    for (const k of Object.keys(all ?? {})) if (k.startsWith(p)) this.map.set(k.slice(p.length), BrewingStandState.from(all![k]));
  }
}
